import { useState } from 'react'
import { cn } from '@/shared/lib/cn'
import { IconChevron, IconFile } from './ToolIcons'
import { TOOL_CONFIGS, SKIP_KEYS } from './toolConfigs'

interface InlineToolCallProps {
  name: string
  args: Record<string, unknown>
  result?: { content: string; isError: boolean } | null
}

export function InlineToolCall({ name, args, result }: InlineToolCallProps) {
  const [expanded, setExpanded] = useState(false)
  const config = TOOL_CONFIGS[name.toLowerCase()] ?? { Icon: IconFile, color: 'text-white/40' }
  const { Icon, color } = config

  const filteredArgs = Object.entries(args).filter(([key]) => !SKIP_KEYS.has(key))

  const formatValue = (value: unknown): string => {
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    return JSON.stringify(value, null, 2)
  }

  // Build a short preview from the first meaningful arg
  const previewArg = filteredArgs[0]
  const preview = previewArg ? formatValue(previewArg[1]) : ''
  const previewTruncated = preview.length > 60 ? preview.slice(0, 60) + '…' : preview

  return (
    <div className="my-1.5 rounded-lg border border-white/10 bg-white/[0.03] overflow-hidden text-xs max-w-[85%]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-white/5 transition-colors"
      >
        <IconChevron className="w-3 h-3 flex-shrink-0 text-white/30" expanded={expanded} />
        <Icon className={cn('w-3.5 h-3.5 flex-shrink-0', color)} />
        <span className="font-semibold text-white/70">{name}</span>
        {!expanded && previewTruncated && (
          <span className="text-white/35 font-mono truncate">{previewTruncated}</span>
        )}
      </button>

      {expanded && (
        <div className="border-t border-white/5 px-3 py-2 max-h-64 overflow-y-auto">
          {filteredArgs.length === 0 ? (
            <span className="text-white/30 italic">no arguments</span>
          ) : (
            <div className="space-y-1.5">
              {filteredArgs.map(([key, value]) => {
                const formatted = formatValue(value)
                const isLong = formatted.length > 120 || formatted.includes('\n')
                return (
                  <div key={key}>
                    <span className="text-white/35">{key} </span>
                    {isLong ? (
                      <pre className="mt-0.5 text-white/65 whitespace-pre-wrap break-all font-mono leading-snug">
                        {formatted}
                      </pre>
                    ) : (
                      <span className="text-white/65 font-mono break-all">{formatted}</span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {result && (
            <>
              <div className="border-t border-white/5 my-2" />
              <div className="text-[10px] text-white/30 mb-1 font-mono">↩ Result</div>
              <pre className={cn('text-xs whitespace-pre-wrap break-all leading-snug', result.isError ? 'text-red-400/70' : 'text-white/60')}>
                {result.content || '(empty)'}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  )
}
